/**
 * Cookie Preferences Modal
 *
 * Modal for managing cookie preferences by category.
 * Features:
 * - Category toggles with descriptions and impact hints
 * - Necessary category locked ON
 * - Focus trap and keyboard navigation
 * - Accessible (aria labels, role="dialog")
 * - Dark/light theme support with proper contrast
 * - Consent version display for transparency
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { useCookieConsent, CONSENT_VERSION } from '../../contexts/CookieConsentContext'
import { X, Shield, Palette, BarChart3, Megaphone, Lock } from 'lucide-react'
import type { EditableCategories } from '../../lib/consent/types'

interface CategoryConfig {
  id: keyof EditableCategories | 'necessary'
  name: string
  description: string
  hint?: string
  icon: React.ReactNode
  iconColorClass?: string
  locked?: boolean
}

// Order: Necessary → Analytics → Functional → Marketing
const CATEGORIES: CategoryConfig[] = [
  {
    id: 'necessary',
    name: 'Strictly Necessary',
    description:
      'Essential for authentication, security, and stability. Includes privacy-safe error tracking (Sentry) to fix bugs and maintain service quality. Cannot be disabled.',
    icon: <Shield className="w-5 h-5" />,
    iconColorClass: 'text-gray-500 dark:text-slate-400',
    locked: true,
  },
  {
    id: 'analytics',
    name: 'Performance & Improvements',
    description:
      'Helps identify bugs and performance issues to improve reliability and feature decisions. Anonymous usage data only, never used for advertising.',
    hint: "Disabling this won't affect core features.",
    icon: <BarChart3 className="w-5 h-5" />,
    iconColorClass: 'text-blue-600 dark:text-blue-400',
  },
  {
    id: 'functional',
    name: 'Functional',
    description:
      'Remember your preferences like theme choice, dashboard settings, and dismissed notifications.',
    hint: 'Your preferences will reset if disabled.',
    icon: <Palette className="w-5 h-5" />,
    iconColorClass: 'text-purple-600 dark:text-purple-400',
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description:
      'Used for advertising and conversion tracking with Google Ads. Helps us measure campaign effectiveness.',
    hint: "Disabling this won't affect pricing or account access.",
    icon: <Megaphone className="w-5 h-5" />,
    iconColorClass: 'text-orange-600 dark:text-orange-400',
  },
]

export default function CookiePreferencesModal() {
  const { showModal, closeModal, consent, acceptAll, rejectAll, savePreferences } =
    useCookieConsent()

  const modalRef = useRef<HTMLDivElement>(null)
  const firstFocusableRef = useRef<HTMLButtonElement>(null)

  // Local state for category toggles
  const [categories, setCategories] = useState<EditableCategories>({
    functional: consent?.categories.functional ?? false,
    analytics: consent?.categories.analytics ?? false,
    marketing: consent?.categories.marketing ?? false,
  })

  // Sync local state when consent changes
  useEffect(() => {
    if (consent) {
      setCategories({
        functional: consent.categories.functional,
        analytics: consent.categories.analytics,
        marketing: consent.categories.marketing,
      })
    } else {
      setCategories({
        functional: false,
        analytics: false,
        marketing: false,
      })
    }
  }, [consent, showModal])

  // Handle ESC key
  useEffect(() => {
    if (!showModal) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showModal, closeModal])

  // Focus trap
  useEffect(() => {
    if (!showModal || !modalRef.current) return

    const modal = modalRef.current
    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    // Focus first element on open
    firstElement?.focus()

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement?.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement?.focus()
        }
      }
    }

    document.addEventListener('keydown', handleTabKey)
    return () => document.removeEventListener('keydown', handleTabKey)
  }, [showModal])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [showModal])

  const handleToggle = useCallback((id: keyof EditableCategories) => {
    setCategories((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }, [])

  const handleSave = useCallback(() => {
    savePreferences(categories)
  }, [savePreferences, categories])

  // Quick choice: Enable only analytics (no marketing)
  const handleEnableAnalyticsOnly = useCallback(() => {
    savePreferences({
      functional: consent?.categories.functional ?? false,
      analytics: true,
      marketing: false,
    })
  }, [consent?.categories.functional, savePreferences])

  if (!showModal) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeModal}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl dark:shadow-black/40 w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col animate-scale-in border border-gray-200 dark:border-white/10"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10">
          <h2
            id="cookie-modal-title"
            className="text-lg font-semibold text-gray-900 dark:text-white"
          >
            Cookie Preferences
          </h2>
          <button
            ref={firstFocusableRef}
            onClick={closeModal}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
            Help us improve Optioneer with privacy-friendly analytics. No ads, no selling data. You
            can reject or customize anytime.{' '}
            <a
              href="/cookies"
              className="text-brand-600 dark:text-amber-400 hover:underline"
              onClick={closeModal}
            >
              View our Cookie Policy
            </a>
          </p>

          {/* Quick Choices */}
          <div className="mb-6 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-white/10">
            <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
              Quick choices
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleEnableAnalyticsOnly}
                className="px-3 py-2 text-xs font-medium text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-300 dark:border-slate-600 rounded-lg transition-colors"
              >
                Allow performance analytics
              </button>
              <button
                onClick={acceptAll}
                className="px-3 py-2 text-xs font-medium text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-300 dark:border-slate-600 rounded-lg transition-colors"
              >
                Accept All
              </button>
              <button
                onClick={rejectAll}
                className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Reject All
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-400 dark:text-slate-500">
              No ads. No selling of data.
            </p>
          </div>

          {/* Category toggles */}
          <div className="space-y-3">
            {CATEGORIES.map((category) => (
              <div
                key={category.id}
                className={`p-4 rounded-xl border transition-colors ${
                  category.locked
                    ? 'bg-gray-50 dark:bg-slate-800/30 border-gray-200 dark:border-white/5'
                    : 'bg-white dark:bg-slate-800/50 border-gray-200 dark:border-white/10 hover:border-brand-300 dark:hover:border-amber-600/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-slate-700/50 ${
                      category.iconColorClass || 'text-brand-600 dark:text-amber-400'
                    }`}
                  >
                    {category.icon}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        {category.name}
                        {category.locked && (
                          <Lock className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500" />
                        )}
                      </h3>

                      {/* Toggle switch */}
                      <button
                        type="button"
                        role="switch"
                        aria-checked={
                          category.locked
                            ? true
                            : categories[category.id as keyof EditableCategories]
                        }
                        aria-label={`${category.name} cookies`}
                        disabled={category.locked}
                        onClick={() =>
                          !category.locked && handleToggle(category.id as keyof EditableCategories)
                        }
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 dark:focus:ring-amber-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 ${
                          category.locked
                            ? 'bg-brand-600 dark:bg-amber-500 cursor-not-allowed' // Locked = ON and disabled
                            : categories[category.id as keyof EditableCategories]
                              ? 'bg-brand-600 dark:bg-amber-500'
                              : 'bg-gray-300 dark:bg-slate-600'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            category.locked || categories[category.id as keyof EditableCategories]
                              ? 'translate-x-5'
                              : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                      {category.description}
                    </p>

                    {/* Impact hint microcopy */}
                    {category.hint && (
                      <p className="mt-1.5 text-xs text-gray-400 dark:text-slate-500 italic">
                        {category.hint}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-slate-800/30">
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={rejectAll}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600 rounded-lg transition-colors"
            >
              Reject All
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600 rounded-lg transition-colors"
            >
              Save Preferences
            </button>
            <button
              onClick={acceptAll}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 dark:bg-amber-500 dark:hover:bg-amber-600 rounded-lg transition-colors shadow-sm"
            >
              Accept All
            </button>
          </div>

          {/* Consent version for transparency */}
          <p className="mt-3 text-center text-xs text-gray-400 dark:text-slate-500">
            Consent version {CONSENT_VERSION}
          </p>
        </div>
      </div>
    </div>
  )
}
